import React, { useState, useMemo } from 'react';
import { Layout, Typography, Card, Row, Col, Pagination, Button, Tabs, Select, Input } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { FaBookOpen } from 'react-icons/fa';
import '../../assets/styles/JectHubChallenges.css';

const { Content } = Layout;
const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const challenges = {
  Technology: [
    { 
      title: "AI Image Recognition", 
      description: "Develop an AI model to recognize objects in images with high accuracy. This challenge requires expertise in computer vision and deep learning frameworks like TensorFlow or PyTorch to process real-world datasets, ensuring robust performance across diverse scenarios.", 
      daysLeft: 20, 
      price: "$500", 
      country: "Kenya" 
    },
    { 
      title: "Cybersecurity Challenge", 
      description: "Identify vulnerabilities in a simulated system and propose mitigation strategies. Participants will analyze network logs, detect intrusions, and enhance security using AI-driven anomaly detection techniques, delivering actionable insights for system hardening.", 
      daysLeft: 17, 
      price: "$400", 
      country: "Nigeria" 
    },
    { 
      title: "Blockchain Innovation", 
      description: "Develop a decentralized application (DApp) leveraging blockchain technology. Focus on creating a secure, scalable solution with smart contracts for applications like supply chain tracking or digital identity verification, integrating with platforms like Ethereum or Hyperledger.", 
      daysLeft: 30, 
      price: "$700", 
      country: "South Africa" 
    },
    { 
      title: "Quantum Computing Experiment", 
      description: "Create a quantum algorithm to solve real-world problems, such as optimization or cryptography. Use platforms like IBM Qiskit to simulate quantum circuits, demonstrating practical applications with measurable improvements over classical methods.", 
      daysLeft: 25, 
      price: "$850", 
      country: "Egypt" 
    },
    { 
      title: "AI Chatbot Development (Beginner)", 
      description: "Create a simple chatbot capable of basic conversations or answering predefined questions using Python's NLTK or ChatterBot. This beginner-level project requires minimal costs, primarily time and computational resources, and offers hands-on NLP experience.", 
      daysLeft: 21, 
      price: "$300", 
      country: "Kenya" 
    },
    { 
      title: "Image Classification (Beginner)", 
      description: "Build a model to classify images into categories (e.g., cats vs. dogs) using datasets like CIFAR-10 or MNIST. This beginner project requires a computer with sufficient processing power; cloud solutions may incur costs, offering practical computer vision skills.", 
      daysLeft: 28, 
      price: "$350", 
      country: "Nigeria" 
    },
    { 
      title: "Predictive Text Generator (Intermediate)", 
      description: "Develop a model that suggests the next word in a sentence, akin to autocomplete, using RNNs or LSTM networks with TensorFlow or Keras. This intermediate project needs computational resources for training, enhancing skills in sequence modeling.", 
      daysLeft: 35, 
      price: "$600", 
      country: "South Africa" 
    },
    { 
      title: "Stock Price Prediction (Intermediate)", 
      description: "Create a system to forecast stock prices based on historical data using machine learning. Access to stock data is required; this intermediate project emphasizes educational value due to real-world complexity, needing computational resources.", 
      daysLeft: 42, 
      price: "$650", 
      country: "Ghana" 
    },
    { 
      title: "Voice-Based Virtual Assistant (Advanced)", 
      description: "Build a virtual assistant to execute voice commands (e.g., web search, reminders) using NLP and speech recognition with tools like Google’s Speech-to-Text API. This advanced project requires significant resources and may involve API costs.", 
      daysLeft: 70, 
      price: "$1000", 
      country: "Egypt" 
    },
    { 
      title: "AI-Powered News Summarizer (Advanced)", 
      description: "Develop a tool to condense news articles into concise summaries using NLP and possibly pre-trained models. This advanced project requires access to diverse news sources and computational power for robust training.", 
      daysLeft: 49, 
      price: "$900", 
      country: "Kenya" 
    },
    { 
      title: "IoT Smart Home Automation", 
      description: "Design a smart home automation system using IoT technologies. Implement AI to optimize energy usage, security, and convenience, with real-time monitoring via a mobile app, leveraging protocols like MQTT or Zigbee for connectivity.", 
      daysLeft: 40, 
      price: "$750", 
      country: "Ethiopia" 
    },
    { 
      title: "Drone Technology for Disaster Relief", 
      description: "Develop AI-driven drones for search and rescue missions in disaster zones. Focus on autonomous navigation, object detection, and real-time data transmission to aid emergency response teams, using tools like ROS and computer vision libraries.", 
      daysLeft: 45, 
      price: "$1,000", 
      country: "Nigeria" 
    },
    { 
      title: "AI-Powered Traffic Management", 
      description: "Create AI solutions to optimize urban traffic flow by analyzing real-time data from cameras and sensors. Develop predictive models to reduce congestion and improve road safety, integrating with traffic management systems.", 
      daysLeft: 38, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "Augmented Reality for Education", 
      description: "Develop AR applications to enhance educational experiences, such as interactive 3D models for science lessons. Ensure compatibility with mobile devices and focus on student engagement using frameworks like Unity or ARKit.", 
      daysLeft: 30, 
      price: "$650", 
      country: "Rwanda" 
    },
    { 
      title: "AI for Predictive Maintenance", 
      description: "Create AI models to predict equipment failures in industrial settings. Use sensor data and machine learning to minimize downtime and optimize maintenance schedules, delivering actionable predictive insights.", 
      daysLeft: 22, 
      price: "$700", 
      country: "Tanzania" 
    },
    { 
      title: "5G Network Optimization", 
      description: "Develop solutions to optimize 5G network performance, focusing on latency reduction and bandwidth allocation using AI-driven analytics. Test with real-world 5G data to ensure practical applicability.", 
      daysLeft: 25, 
      price: "$800", 
      country: "Kenya" 
    },
    { 
      title: "AI in Robotics", 
      description: "Build AI-driven robots for applications like warehouse automation or healthcare support. Focus on navigation, task execution, and human-robot interaction using frameworks like ROS and reinforcement learning.", 
      daysLeft: 30, 
      price: "$900", 
      country: "Nigeria" 
    },
    { 
      title: "Edge Computing Solutions", 
      description: "Develop edge computing solutions for real-time data processing in IoT devices. Ensure low latency and high efficiency for applications like smart cities or autonomous vehicles, using edge AI frameworks.", 
      daysLeft: 35, 
      price: "$750", 
      country: "South Africa" 
    },
    // New Technology Challenges
    { 
      title: "AI-Powered Smart Home Security", 
      description: "Develop an AI-driven security system for smart homes, using computer vision and machine learning to detect and prevent intrusions.", 
      daysLeft: 30, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "Quantum Computing for Cryptography", 
      description: "Create quantum algorithms to enhance cryptographic security, using platforms like IBM Qiskit to simulate quantum circuits.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Egypt" 
    },
    { 
      title: "AI for Autonomous Drones", 
      description: "Develop AI models for autonomous drone navigation, focusing on obstacle avoidance and path planning using computer vision.", 
      daysLeft: 35, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Smart Grid Optimization", 
      description: "Develop AI models to optimize energy distribution in smart grids, ensuring efficient energy usage and reducing costs.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Egypt" 
    },
    { 
      title: "AI for Autonomous Vehicles", 
      description: "Create AI-driven systems for autonomous vehicles, focusing on safety, navigation, and traffic management.", 
      daysLeft: 45, 
      price: "$900", 
      country: "South Africa" 
    },
  ],
  Health: [
    { 
      title: "Healthcare AI Innovation", 
      description: "Create an AI solution to improve healthcare services, such as patient triage or resource allocation. Leverage machine learning to analyze medical data and enhance care delivery, integrating with hospital management systems.", 
      daysLeft: 19, 
      price: "$600", 
      country: "Kenya" 
    },
    { 
      title: "Mental Health App Challenge", 
      description: "Build a digital solution for mental health support, integrating AI chatbots for therapy and mood tracking features to assist users in managing stress and anxiety, with a focus on user privacy and accessibility.", 
      daysLeft: 26, 
      price: "$650", 
      country: "Uganda" 
    },
    { 
      title: "Disaster Prediction and Management", 
      description: "Create AI tools to predict natural disasters and manage responses effectively. Use historical data and real-time inputs to improve preparedness and reduce impact, delivering predictive models for disaster response teams.", 
      daysLeft: 20, 
      price: "$750", 
      country: "Tanzania" 
    },
    { 
      title: "Telemedicine Platform", 
      description: "Develop a telemedicine platform for rural healthcare access, incorporating AI diagnostics and video consultation features to connect patients with doctors remotely, ensuring scalability and reliability.", 
      daysLeft: 23, 
      price: "$700", 
      country: "Rwanda" 
    },
    { 
      title: "AI for Disease Diagnosis", 
      description: "Develop an AI-based model to diagnose diseases from medical images like X-rays or MRIs. Ensure high accuracy and integration with existing healthcare systems using deep learning techniques.", 
      daysLeft: 30, 
      price: "$800", 
      country: "Kenya" 
    },
    { 
      title: "AI-Powered Healthcare Data Analytics", 
      description: "Design AI systems to analyze healthcare data for better decision-making, focusing on patient outcomes, resource optimization, and predictive analytics, with real-time reporting capabilities.", 
      daysLeft: 40, 
      price: "$950", 
      country: "Ghana" 
    },
    { 
      title: "Smart Medical Device Integration", 
      description: "Build a system to integrate IoT-based medical devices for real-time health monitoring, using AI to interpret data and alert healthcare providers, ensuring seamless connectivity and accuracy.", 
      daysLeft: 50, 
      price: "$1000", 
      country: "South Africa" 
    },
    { 
      title: "Wearable AI for Health Monitoring", 
      description: "Design AI-powered wearable tech for real-time health tracking, including heart rate, sleep patterns, and activity levels, with predictive alerts for anomalies using advanced algorithms.", 
      daysLeft: 35, 
      price: "$850", 
      country: "Ethiopia" 
    },
    { 
      title: "AI for Personalized Medicine", 
      description: "Develop AI solutions to create personalized treatment plans based on patient data, including genetics, lifestyle, and medical history, integrating with electronic health records.", 
      daysLeft: 28, 
      price: "$700", 
      country: "Kenya" 
    },
    { 
      title: "AI in Medical Research", 
      description: "Use AI to accelerate medical research and drug discovery by analyzing large datasets and identifying potential treatment pathways, with a focus on statistical validation.", 
      daysLeft: 30, 
      price: "$800", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Patient Flow Optimization", 
      description: "Create AI models to optimize patient flow in hospitals, reducing wait times and improving resource allocation using predictive analytics and real-time data.", 
      daysLeft: 25, 
      price: "$750", 
      country: "South Africa" 
    },
    { 
      title: "AI for Remote Patient Monitoring", 
      description: "Develop AI-driven systems for remote patient monitoring, enabling continuous care for chronic conditions using wearable devices and telemedicine, with secure data transmission.", 
      daysLeft: 35, 
      price: "$850", 
      country: "Ghana" 
    },
    { 
      title: "AI for Healthcare Fraud Detection", 
      description: "Build AI systems to detect and prevent healthcare fraud by analyzing billing patterns and identifying anomalies, ensuring compliance with regulatory standards.", 
      daysLeft: 40, 
      price: "$900", 
      country: "Kenya" 
    },
    // New Health Challenges
    { 
      title: "AI for Medical Image Analysis", 
      description: "Create AI models to analyze medical images for early disease detection, using deep learning techniques to improve diagnostic accuracy.", 
      daysLeft: 30, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Personalized Treatment Plans", 
      description: "Develop AI solutions to create personalized treatment plans based on patient data, integrating with electronic health records.", 
      daysLeft: 35, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Mental Health Monitoring", 
      description: "Build AI-driven tools to monitor mental health, using NLP and sentiment analysis to track mood and provide early intervention.", 
      daysLeft: 40, 
      price: "$900", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Medical Research", 
      description: "Use AI to analyze medical research data, identifying patterns and insights to accelerate drug discovery and treatment development.", 
      daysLeft: 40, 
      price: "$900", 
      country: "Kenya" 
    },
    { 
      title: "AI for Patient Monitoring", 
      description: "Develop AI-driven patient monitoring systems, using IoT devices to track vital signs and provide real-time alerts to healthcare providers.", 
      daysLeft: 45, 
      price: "$950", 
      country: "Nigeria" 
    },
  ],
  Agriculture: [
    { 
      title: "Smart Farming Challenge", 
      description: "Design AI-driven solutions for precision farming to optimize crop yields and resource use. Leverage sensor data and machine learning to provide real-time recommendations for farmers.", 
      daysLeft: 28, 
      price: "$750", 
      country: "Kenya" 
    },
    { 
      title: "Food Security Innovation", 
      description: "Develop a solution to enhance food security in rural areas using AI to analyze agricultural data and predict supply shortages, integrating with local farming systems.", 
      daysLeft: 22, 
      price: "$700", 
      country: "Nigeria" 
    },
    { 
      title: "AI-Powered Irrigation System", 
      description: "Create an AI system to optimize irrigation for farming by analyzing weather, soil, and crop data. Ensure efficient water use and scalability for smallholder farmers.", 
      daysLeft: 25, 
      price: "$720", 
      country: "South Africa" 
    },
    { 
      title: "Livestock Health Monitoring", 
      description: "Build a system for monitoring livestock health using AI and IoT sensors. Provide predictive alerts for disease outbreaks and improve farm management practices.", 
      daysLeft: 27, 
      price: "$680", 
      country: "Ethiopia" 
    },
    { 
      title: "AI-Driven Agricultural Supply Chain", 
      description: "Optimize agricultural supply chains using AI and data analytics to reduce waste and improve delivery efficiency, integrating with blockchain for transparency.", 
      daysLeft: 32, 
      price: "$800", 
      country: "Ghana" 
    },
    { 
      title: "Farm-to-Table Blockchain Solution", 
      description: "Build a decentralized solution to track agricultural products from farm to table using blockchain. Ensure traceability and authenticity with smart contracts.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Pest Control", 
      description: "Develop an AI solution to detect and control pests in farms using image recognition and predictive modeling, reducing reliance on chemical pesticides.", 
      daysLeft: 38, 
      price: "$780", 
      country: "Uganda" 
    },
    { 
      title: "AI for Crop Yield Prediction", 
      description: "Create AI models to predict crop yields and optimize farming practices by analyzing historical and real-time agricultural data, aiding farmer decision-making.", 
      daysLeft: 30, 
      price: "$750", 
      country: "Kenya" 
    },
    { 
      title: "AI for Soil Health Analysis", 
      description: "Develop AI solutions to analyze soil health and provide recommendations for fertilizers and crop rotation, using sensor data and machine learning.", 
      daysLeft: 25, 
      price: "$700", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Agricultural Market Analysis", 
      description: "Use AI to analyze market trends and optimize agricultural sales strategies, providing farmers with predictive insights for pricing and demand.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Sustainable Agriculture", 
      description: "Develop AI-driven solutions for sustainable farming practices, focusing on resource conservation and climate adaptation using real-time data analytics.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Ghana" 
    },
    { 
      title: "AI for Agricultural Risk Management", 
      description: "Create AI models to assess and manage risks in agriculture, such as weather impacts or pest outbreaks, delivering actionable mitigation strategies.", 
      daysLeft: 38, 
      price: "$780", 
      country: "Ethiopia" 
    },
    // New Agriculture Challenges
    { 
      title: "AI for Crop Disease Detection", 
      description: "Develop AI models to detect crop diseases using image recognition, providing early alerts to farmers.", 
      daysLeft: 30, 
      price: "$750", 
      country: "Ethiopia" 
    },
    { 
      title: "AI for Agricultural Market Forecasting", 
      description: "Create AI-driven systems to forecast agricultural market trends, helping farmers make informed decisions.", 
      daysLeft: 35, 
      price: "$800", 
      country: "Ghana" 
    },
    { 
      title: "AI for Sustainable Farming Practices", 
      description: "Develop AI solutions to promote sustainable farming practices, analyzing data to optimize resource use and reduce environmental impact.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Precision Agriculture", 
      description: "Create AI models for precision agriculture, using satellite imagery and sensor data to optimize crop management and reduce resource usage.", 
      daysLeft: 40, 
      price: "$850", 
      country: "South Africa" 
    },
    { 
      title: "AI for Agricultural Robotics", 
      description: "Develop AI-driven robots for agricultural tasks, such as planting, harvesting, and weeding, to improve efficiency and reduce labor costs.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Kenya" 
    },
  ],
  Environment: [
    { 
      title: "Climate Change Data Analysis", 
      description: "Use data to assess climate change impact and propose solutions. Leverage AI to analyze large environmental datasets and predict trends for policy recommendations.", 
      daysLeft: 17, 
      price: "$550", 
      country: "Kenya" 
    },
    { 
      title: "Sustainable Energy Solutions", 
      description: "Develop technology to optimize renewable energy usage, such as solar or wind power, using AI to improve efficiency and grid integration.", 
      daysLeft: 21, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "Waste Management Innovation", 
      description: "Develop smart waste management solutions using AI to optimize collection routes and recycling processes, reducing environmental impact.", 
      daysLeft: 24, 
      price: "$600", 
      country: "Ghana" 
    },
    { 
      title: "Ocean Conservation AI", 
      description: "Create AI models to predict and conserve ocean ecosystems by analyzing marine data and tracking pollution or species health.", 
      daysLeft: 30, 
      price: "$850", 
      country: "Nigeria" 
    },
    { 
      title: "Smart City Sustainability", 
      description: "Develop AI-driven solutions for smart city sustainability, optimizing energy, water, and transportation systems with real-time data analytics.", 
      daysLeft: 40, 
      price: "$950", 
      country: "Egypt" 
    },
    { 
      title: "AI for Renewable Energy Optimization", 
      description: "Design AI algorithms to optimize renewable energy production and consumption, integrating with IoT devices for real-time adjustments.", 
      daysLeft: 45, 
      price: "$1000", 
      country: "Kenya" 
    },
    { 
      title: "AI for Wildlife Protection", 
      description: "Develop AI models to prevent poaching and track endangered species using image recognition and geospatial data analysis.", 
      daysLeft: 35, 
      price: "$900", 
      country: "Tanzania" 
    },
    { 
      title: "AI for Air Quality Monitoring", 
      description: "Create AI systems to monitor and predict air quality using sensor networks, providing actionable insights for pollution reduction.", 
      daysLeft: 28, 
      price: "$700", 
      country: "Kenya" 
    },
    { 
      title: "AI for Water Resource Management", 
      description: "Develop AI solutions for efficient water resource management, analyzing usage patterns and predicting shortages for sustainable planning.", 
      daysLeft: 30, 
      price: "$750", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Forest Fire Detection", 
      description: "Create AI models to detect and predict forest fires using satellite imagery and weather data, aiding rapid response efforts.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Biodiversity Conservation", 
      description: "Develop AI-driven solutions for biodiversity conservation by analyzing ecological data and proposing habitat preservation strategies.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Ghana" 
    },
    { 
      title: "AI for Climate Resilience", 
      description: "Create AI models to build resilience against climate change impacts, such as floods or droughts, using predictive analytics and adaptation plans.", 
      daysLeft: 38, 
      price: "$900", 
      country: "Ethiopia" 
    },
    // New Environment Challenges
    { 
      title: "AI for Climate Change Mitigation", 
      description: "Create AI models to analyze climate data and propose mitigation strategies, focusing on reducing carbon emissions.", 
      daysLeft: 35, 
      price: "$850", 
      country: "South Africa" 
    },
    { 
      title: "AI for Biodiversity Conservation", 
      description: "Develop AI-driven solutions for biodiversity conservation, using image recognition and geospatial data to protect endangered species.", 
      daysLeft: 40, 
      price: "$900", 
      country: "Kenya" 
    },
    { 
      title: "AI for Sustainable Water Management", 
      description: "Create AI systems to optimize water usage, analyzing data to prevent wastage and ensure sustainable water management.", 
      daysLeft: 45, 
      price: "$950", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Environmental Monitoring", 
      description: "Create AI-driven systems for real-time environmental monitoring, analyzing data from sensors to track pollution and climate change impacts.", 
      daysLeft: 40, 
      price: "$850", 
      country: "South Africa" 
    },
    { 
      title: "AI for Wildlife Conservation", 
      description: "Develop AI models to protect wildlife, using image recognition and geospatial data to track and monitor endangered species.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Kenya" 
    },
  ],
  Education: [
    { 
      title: "Digital Literacy for All", 
      description: "Develop an innovative platform to promote digital literacy, using AI to personalize learning paths and improve accessibility for underserved communities.", 
      daysLeft: 18, 
      price: "$400", 
      country: "Kenya" 
    },
    { 
      title: "AI for Special Needs Education", 
      description: "Create tools to support children with disabilities using AI for adaptive learning, speech recognition, and personalized content delivery.", 
      daysLeft: 30, 
      price: "$900", 
      country: "Tanzania" 
    },
    { 
      title: "Remote Learning Platform", 
      description: "Develop an AI-powered platform for remote learning, integrating intelligent tutoring systems and real-time feedback for students and educators.", 
      daysLeft: 25, 
      price: "$750", 
      country: "Uganda" 
    },
    { 
      title: "Gamified Learning Solution", 
      description: "Create a gamified platform to engage students in interactive learning, using AI to adapt challenges and rewards based on individual progress.", 
      daysLeft: 35, 
      price: "$800", 
      country: "Nigeria" 
    },
    { 
      title: "AI Tutor for Personalized Learning", 
      description: "Design an AI tutor to provide personalized learning experiences for students, leveraging NLP and machine learning to address individual needs.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "Virtual Reality for Education", 
      description: "Develop an immersive VR learning experience for students, using AI to create dynamic educational scenarios in frameworks like Unity or Unreal Engine.", 
      daysLeft: 38, 
      price: "$920", 
      country: "Ghana" 
    },
    { 
      title: "AI for Educational Assessment", 
      description: "Create AI-driven assessment tools to provide personalized feedback, analyzing student performance data to improve learning outcomes.", 
      daysLeft: 28, 
      price: "$700", 
      country: "Kenya" 
    },
    { 
      title: "AI for Curriculum Development", 
      description: "Develop AI solutions to enhance curriculum development by analyzing educational trends and student needs, ensuring adaptive content creation.", 
      daysLeft: 30, 
      price: "$750", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Student Engagement", 
      description: "Create AI tools to improve student engagement in the classroom, using predictive analytics to tailor interactive learning experiences.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Lifelong Learning", 
      description: "Develop AI-driven platforms for continuous learning, offering personalized course recommendations and skill development tracking.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Ghana" 
    },
    { 
      title: "AI for Educational Research", 
      description: "Use AI to analyze educational data and inform policy decisions, providing insights into learning trends and effectiveness of teaching methods.", 
      daysLeft: 38, 
      price: "$900", 
      country: "Ethiopia" 
    },
    // New Education Challenges
    { 
      title: "AI for Adaptive Learning Platforms", 
      description: "Develop AI-driven adaptive learning platforms, using machine learning to personalize educational content for students.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Educational Content Creation", 
      description: "Create AI tools to generate educational content, using natural language processing to create engaging and interactive learning materials.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Student Performance Analysis", 
      description: "Develop AI models to analyze student performance data, providing insights for educators to improve teaching strategies.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Personalized Learning", 
      description: "Develop AI-driven platforms for personalized learning, using adaptive learning techniques to enhance educational outcomes.", 
      daysLeft: 40, 
      price: "$850", 
      country: "South Africa" 
    },
    { 
      title: "AI for Educational Analytics", 
      description: "Create AI models to analyze educational data, providing insights into student performance and improving teaching strategies.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Kenya" 
    },
  ],
  Finance: [
    { 
      title: "Machine Learning Competition", 
      description: "Train a machine learning model to predict financial trends, using historical market data and advanced algorithms to achieve high accuracy.", 
      daysLeft: 18, 
      price: "$550", 
      country: "Kenya" 
    },
    { 
      title: "Financial Fraud Detection", 
      description: "Create an AI-based fraud detection system for financial transactions, analyzing patterns and anomalies to prevent fraudulent activities in real time.", 
      daysLeft: 20, 
      price: "$700", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Stock Market Prediction", 
      description: "Develop an AI model to predict stock market trends, leveraging time-series analysis and deep learning to provide actionable investment insights.", 
      daysLeft: 25, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "Cryptocurrency Price Prediction", 
      description: "Build a predictive model for cryptocurrency prices using machine learning, analyzing blockchain data and market volatility for accurate forecasts.", 
      daysLeft: 35, 
      price: "$900", 
      country: "Ghana" 
    },
    { 
      title: "AI for Credit Risk Assessment", 
      description: "Design an AI system to assess credit risk for financial institutions, using customer data and predictive analytics to improve lending decisions.", 
      daysLeft: 40, 
      price: "$950", 
      country: "Kenya" 
    },
    { 
      title: "AI for Microfinance Optimization", 
      description: "Develop an AI tool to enhance microfinance lending strategies, analyzing borrower profiles and repayment patterns to reduce risk and increase access.", 
      daysLeft: 30, 
      price: "$880", 
      country: "Ethiopia" 
    },
    { 
      title: "AI for Financial Planning", 
      description: "Create AI-driven financial planning tools for personalized advice, integrating income, expenses, and investment data for tailored strategies.", 
      daysLeft: 28, 
      price: "$700", 
      country: "Kenya" 
    },
    { 
      title: "AI for Investment Management", 
      description: "Develop AI solutions to optimize investment portfolios, using predictive models to balance risk and return based on market conditions.", 
      daysLeft: 30, 
      price: "$750", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Customer Relationship Management", 
      description: "Create AI-driven CRM systems to enhance customer engagement in financial services, personalizing offers and improving retention.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Financial Market Analysis", 
      description: "Develop AI models to analyze financial market trends and insights, providing real-time data for traders and investors.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Ghana" 
    },
    { 
      title: "AI for Risk Management", 
      description: "Create AI-driven risk management solutions for financial institutions, predicting and mitigating risks using advanced analytics.", 
      daysLeft: 38, 
      price: "$900", 
      country: "Ethiopia" 
    },
    // New Finance Challenges
    { 
      title: "AI for Financial Risk Assessment", 
      description: "Create AI-driven risk assessment models, analyzing financial data to predict and mitigate risks.", 
      daysLeft: 35, 
      price: "$800", 
      country: "South Africa" 
    },
    { 
      title: "AI for Investment Strategy Optimization", 
      description: "Develop AI models to optimize investment strategies, using machine learning to analyze market trends and provide actionable insights.", 
      daysLeft: 40, 
      price: "$850", 
      country: "Kenya" 
    },
    { 
      title: "AI for Financial Fraud Prevention", 
      description: "Create AI-driven systems to detect and prevent financial fraud, analyzing transaction data to identify anomalies.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Nigeria" 
    },
    { 
      title: "AI for Financial Forecasting", 
      description: "Develop AI models to forecast financial trends, using machine learning to analyze market data and provide actionable insights.", 
      days_foot: 40, 
      price: "$850", 
      country: "South Africa" 
    },
    { 
      title: "AI for Financial Advisory", 
      description: "Create AI-driven financial advisory tools, providing personalized investment advice and financial planning for individuals.", 
      daysLeft: 45, 
      price: "$900", 
      country: "Kenya" 
    },
  ],
};

const pageSize = 8;

const JectHubChallenges = () => {
  const [currentTab, setCurrentTab] = useState('Technology');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('daysLeft'); // New state for sorting

  const handleTabChange = (key) => {
    setCurrentTab(key);
    setCurrentPage(1);
    setSelectedCountry('');
    setSearchQuery('');
  };

  const handleCountryChange = (value) => {
    setSelectedCountry(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  const currentChallenges = challenges[currentTab] || [];

  const filteredChallenges = useMemo(() => {
    let filtered = [...currentChallenges];
    if (selectedCountry) {
      filtered = filtered.filter((challenge) => challenge.country === selectedCountry);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (challenge) =>
          challenge.title.toLowerCase().includes(query) ||
          challenge.description.toLowerCase().includes(query)
      );
    }
    // Sorting
    filtered.sort((a, b) => {
      if (sortBy === 'daysLeft') return a.daysLeft - b.daysLeft;
      if (sortBy === 'price') {
        return parseFloat(b.price.replace('$', '')) - parseFloat(a.price.replace('$', ''));
      }
      return 0;
    });
    return filtered;
  }, [selectedCountry, searchQuery, sortBy, currentChallenges]);

  const paginatedChallenges = useMemo(() => {
    return filteredChallenges.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [filteredChallenges, currentPage]);

  const uniqueCountries = useMemo(
    () => [...new Set(currentChallenges.map((challenge) => challenge.country))],
    [currentChallenges]
  );

  return (
    <Layout>
      <Content style={{ padding: '50px', background: 'linear-gradient(135deg, #f0f2f5, #e6f7ff)' }}>
        <Title level={2} style={{ fontSize: '24px', display: 'flex', alignItems: 'center', color: '#0050b3', marginBottom: '32px' }}>
          <FaBookOpen style={{ marginRight: '12px', fontSize: '24px' }} /> JectHub Challenges
        </Title>
        <div style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              borderRadius: '6px',
              background: 'linear-gradient(45deg, #0050b3, #003a8c)',
              border: 'none',
              fontWeight: 'bold',
              padding: '0 20px',
              height: '40px',
            }}
          >
            Create New Challenge
          </Button>
          <Input
            placeholder="Search challenges by title or description"
            prefix={<SearchOutlined />}
            value={searchQuery}
            style={{ width: '320px', borderRadius: '6px', height: '40px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
            onChange={handleSearchChange}
          />
          <Select
            allowClear
            placeholder="Filter by Country"
            value={selectedCountry}
            style={{ width: '220px', borderRadius: '6px' }}
            onChange={handleCountryChange}
            dropdownStyle={{ borderRadius: '6px' }}
          >
            <Option value="">All Countries</Option>
            {uniqueCountries.map((country) => (
              <Option key={country} value={country}>
                {country}
              </Option>
            ))}
          </Select>
          <Select
            placeholder="Sort By"
            defaultValue="daysLeft"
            style={{ width: '180px', borderRadius: '6px' }}
            onChange={handleSortChange}
            dropdownStyle={{ borderRadius: '6px' }}
          >
            <Option value="daysLeft">Days Left (Asc)</Option>
            <Option value="price">Prize (High to Low)</Option>
          </Select>
        </div>
        <Tabs
          activeKey={currentTab}
          onChange={handleTabChange}
          className="custom-tabs"
          tabBarStyle={{ fontWeight: '600', color: '#0050b3', borderBottom: '2px solid #0050b3' }}
        >
          {Object.keys(challenges).map((category) => (
            <TabPane tab={category} key={category}>
              <Row gutter={[24, 24]}>
                {paginatedChallenges.length > 0 ? (
                  paginatedChallenges.map((challenge, index) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={index}>
                      <div className="flip-container" style={{ perspective: '1000px', height: '360px' }}>
                        <div className="flip-card">
                          {/* Front Side */}
                          <Card
                            className="flip-card-front"
                            style={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              backfaceVisibility: 'hidden',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                              background: 'linear-gradient(135deg, #ffffff, #f9faff)',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            }}
                            hoverable
                          >
                            <div>
                              <Title level={4} style={{ marginBottom: '12px', color: '#1a1a1a', fontSize: '18px', fontWeight: '600' }}>
                                {challenge.title}
                              </Title>
                              <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                                <strong style={{ color: '#0050b3' }}>Country:</strong> {challenge.country}
                              </Paragraph>
                              <Paragraph style={{ marginBottom: '8px', color: '#595959', fontSize: '14px' }}>
                                <strong style={{ color: '#0050b3' }}>Days Left:</strong> {challenge.daysLeft}
                              </Paragraph>
                              <Paragraph style={{ fontWeight: 'bold', color: '#003a8c', fontSize: '16px', marginBottom: '0' }}>
                                Prize: {challenge.price}
                              </Paragraph>
                            </div>
                            <Button
                              type="primary"
                              href="/signin"
                              style={{
                                background: 'linear-gradient(45deg, #0050b3, #003a8c)',
                                border: 'none',
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                height: '40px',
                                marginTop: '16px',
                                transition: 'all 0.3s ease',
                              }}
                              onMouseEnter={(e) => (e.target.style.background = 'linear-gradient(45deg, #003a8c, #002d62)')}
                              onMouseLeave={(e) => (e.target.style.background = 'linear-gradient(45deg, #0050b3, #003a8c)')}
                            >
                              Participate Now
                            </Button>
                          </Card>
                          {/* Back Side */}
                          <Card
                            className="flip-card-back"
                            style={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              backfaceVisibility: 'hidden',
                              transform: 'rotateY(180deg)',
                              borderRadius: '12px',
                              overflow: 'hidden',
                              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                              background: 'linear-gradient(135deg, #0050b3, #003a8c)',
                              padding: '16px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              color: '#fff',
                            }}
                          >
                            <div>
                              <Title level={4} style={{ marginBottom: '12px', color: '#fff', fontSize: '18px', fontWeight: '600' }}>
                                {challenge.title}
                              </Title>
                              <Paragraph
                                style={{
                                  color: '#e6f7ff',
                                  fontSize: '13px',
                                  height: '180px',
                                  overflow: 'auto',
                                  marginBottom: '12px',
                                  lineHeight: '1.5',
                                }}
                              >
                                {challenge.description}
                              </Paragraph>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                              <Paragraph style={{ fontWeight: 'bold', color: '#fff', fontSize: '16px', marginBottom: '12px' }}>
                                Prize: {challenge.price}
                              </Paragraph>
                              <Button
                                type="primary"
                                href="/signin"
                                style={{
                                  background: '#fff',
                                  color: '#0050b3',
                                  border: 'none',
                                  borderRadius: '6px',
                                  fontWeight: 'bold',
                                  height: '40px',
                                  width: '100%',
                                  transition: 'all 0.3s ease',
                                }}
                                onMouseEnter={(e) => (e.target.style.background = '#f0f0f0')}
                                onMouseLeave={(e) => (e.target.style.background = '#fff')}
                              >
                                Participate Now
                              </Button>
                            </div>
                          </Card>
                        </div>
                      </div>
                    </Col>
                  ))
                ) : (
                  <Col span={24}>
                    <Paragraph style={{ textAlign: 'center', color: '#999', fontSize: '16px' }}>
                      No challenges match your filters. Try adjusting your search, country, or sort options!
                    </Paragraph>
                  </Col>
                )}
              </Row>
              <Pagination
                style={{ marginTop: '32px', textAlign: 'center', fontSize: '16px' }}
                current={currentPage}
                pageSize={pageSize}
                total={filteredChallenges.length}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showQuickJumper
              />
            </TabPane>
          ))}
        </Tabs>
        {/* Flip Card CSS */}
        <style jsx global>{`
          .flip-container {
            perspective: 1000px;
          }
          .flip-card {
            position: relative;
            width: 100%;
            height: 100%;
            transition: transform 0.6s ease;
            transform-style: preserve-3d;
          }
          .flip-container:hover .flip-card {
            transform: rotateY(180deg);
          }
          .flip-card-front,
          .flip-card-back {
            position: absolute;
            width: 100%;
            height: 100%;
            backface-visibility: hidden;
          }
          .flip-card-back {
            transform: rotateY(180deg);
          }
          .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #0050b3 !important;
          }
          .ant-tabs-ink-bar {
            background: #0050b3 !important;
          }
          .ant-pagination-item-active {
            border-color: #0050b3 !important;
          }
          .ant-pagination-item-active a {
            color: #0050b3 !important;
          }
          .ant-select-selector {
            height: 40px !important;
            display: flex !important;
            align-items: center !important;
          }
        `}</style>
      </Content>
    </Layout>
  );
};

export default JectHubChallenges;